import React, { forwardRef, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { EditIcon, Header2, IconButton, Menu, PriorityLowIcon, Tooltip, TreeDotsIcon } from "@activecollab/components";
import classnames from "classnames";
import classNames from "classnames";
import PropTypes from "prop-types";
import { getProjectById } from "~/store/selectors/selector.projects";
import { makeGetTaskIsInConflict } from "~/store/selectors/selector.taskDependencies";
import { getTaskById } from "~/store/selectors/selector.tasks";
import { hasCollaborativePermissionsOnProject } from "~/store/selectors/selector.users";
import { Metadata } from "./Metadata";
import { SingleTaskNavigation } from "./SingleTaskNavigation";
import TaskCompleteCheckbox from "./TaskCompleteCheckboxContainer.jsx";
import TaskConflictTooltipContainer from "./TaskConflictTooltipContainer.jsx";
import TaskNameContainer from "./TaskNameContainer.jsx";
import TaskOptionsContainer from "./TaskOptionsContainer";
import NewCommentsNotification from "../comments/NewCommentsNotification";
import ReminderFormContainer from "../Reminders/ReminderFormContainer";
import HiddenFromClients from "../tasks/Signifiers/HiddenFromClients.jsx";

const Target = forwardRef(({ onClick }, ref) => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t("More options")}>
      <IconButton
        variant="text gray"
        onClick={onClick}
        ref={ref}
        aria-label="More"
      >
        <TreeDotsIcon />
      </IconButton>
    </Tooltip>
  );
});

Target.propTypes = {
  onClick: PropTypes.func,
};

Target.displayName = "Target";

const Header = ({ id, projectId, className, openInModal, handleShowEditTaskForm, isTaskDependenciesEnabled }) => {
  const { t } = useTranslation();
  const task = useSelector((state) => getTaskById(state, id));
  const isInConflictSelector = useMemo(() => makeGetTaskIsInConflict(), []);
  const isInConflict = useSelector((state) => isInConflictSelector(state, id));
  const project = useSelector((state) => getProjectById(state, projectId));

  const [open, setOpen] = useState(false);
  const handleOpen = useCallback(() => setOpen(true), []);
  const handleClose = useCallback(() => setOpen(false), []);

  const [showReminderModal, setShowReminderModal] = useState(false);

  const handleOpenReminder = useCallback(() => {
    handleClose();
    setShowReminderModal(true);
  }, [handleClose]);

  const handleCloseReminder = useCallback(() => {
    setShowReminderModal(false);
  }, []);

  const isImportant = task?.is_important;
  const isCompleted = task?.is_completed;
  const isHiddenFromClients = task?.is_hidden_from_clients;
  const completedOn = task?.completed_on;
  const projectName = project?.name;
  const isLoggedUserElevatedPermissions = useSelector((state) =>
    hasCollaborativePermissionsOnProject(state, projectId)
  );
  const projectUrlPath = project?.url_path || `/projects/${projectId}`;
  const taskNumber = task?.task_number;
  const createdOn = task?.created_on;
  const createdById = task?.created_by_id;
  const createdByName = task?.created_by_name;
  const createdByEmail = task?.created_by_email;

  return (
    <div className={classNames("tw-relative", className)}>
      <div className="tw-flex tw-justify-between tw-mb-2">
        <div
          className={classNames({
            "tw-opacity-60": isCompleted,
          })}
        >
          <TaskCompleteCheckbox
            taskId={id}
            projectId={projectId}
            className={classnames("tw-inline-flex tw-align-middle", {
              "tw-mr-3": !isImportant,
            })}
          />
          {isImportant && (
            <PriorityLowIcon
              fill="var(--red-alert)"
              className="tw-mx-1 tw-align-middle"
            />
          )}
          <Header2
            weight="bold"
            as="span"
            className={classnames("tw-align-middle", {
              "tw-text-red-alert": isImportant,
              "tw-line-through": isCompleted,
            })}
          >
            <TaskNameContainer id={id} />
          </Header2>
          <TaskConflictTooltipContainer
            isInConflict={isTaskDependenciesEnabled ? isInConflict : false}
            className="tw-align-middle tw-ml-2 modal-header-icon"
          />

          <HiddenFromClients
            hiddenFromClients={isHiddenFromClients}
            className="modal-header-icon tw-align-middle"
          />
        </div>
        <div className="tw-flex tw-h-fit tw--mt-1 tw-ml-2">
          {!openInModal && !completedOn ? (
            <SingleTaskNavigation
              projectId={projectId}
              taskId={id}
            />
          ) : null}
          {isLoggedUserElevatedPermissions ? (
            <Tooltip title={t("Edit")}>
              <IconButton
                aria-label="Edit"
                className="tw-mr-1"
                variant="text gray"
                onClick={handleShowEditTaskForm}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          ) : null}
          <Menu
            target={<Target />}
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
          >
            <TaskOptionsContainer
              taskId={id}
              projectId={projectId}
              onChooseRecurringTask={handleClose}
              onCopyPermalinkToClipboard={handleClose}
              onTrash={handleClose}
              onDuplicate={handleClose}
              onMoveCopyToProject={handleClose}
              onCreateRecurringTask={handleClose}
              onAddReminder={handleOpenReminder}
            />
          </Menu>
        </div>
      </div>
      <Metadata
        id={id}
        projectName={projectName}
        projectUrlPath={projectUrlPath}
        taskNumber={taskNumber}
        createdById={createdById}
        createdOn={createdOn}
        createdByName={createdByName}
        createdByEmail={createdByEmail}
      />
      <NewCommentsNotification
        parentType="Task"
        parentId={id}
      />
      {showReminderModal ? (
        <ReminderFormContainer
          open={showReminderModal}
          parent={task}
          members={project?.members.toArray()}
          onCancel={handleCloseReminder}
          onSubmit={handleCloseReminder}
        />
      ) : null}
    </div>
  );
};

Header.propTypes = {
  id: PropTypes.number,
  isTaskDependenciesEnabled: PropTypes.bool,
  projectId: PropTypes.number,
  className: PropTypes.string,
  openInModal: PropTypes.bool,
  handleShowEditTaskForm: PropTypes.func,
};

Header.displayName = "TaskModalHeader";

export default Header;
